import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface DepartmentProgressTrendsChartProps {
  data: Array<{
    month: string;
    [key: string]: string | number; // Dynamic department names as keys
  }>;
  departments: string[];
  loading?: boolean;
}

const DepartmentProgressTrendsChart: React.FC<DepartmentProgressTrendsChartProps> = ({ data, departments, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Progress Trends</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0 || !departments || departments.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Progress Trends</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No progress trend data available
        </div>
      </div>
    );
  }

  const formatMonth = (month: string) => {
    const date = new Date(month);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
  ];

  const getDepartmentColor = (index: number) => colors[index % colors.length];

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Progress Trends</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }}
              tickFormatter={formatMonth}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <Tooltip 
              formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]}
              labelFormatter={(label) => `Month: ${formatMonth(label)}`}
            />
            <Legend />
            {departments.map((dept, index) => (
              <Line
                key={dept}
                type="monotone"
                dataKey={dept}
                stroke={getDepartmentColor(index)}
                strokeWidth={2}
                name={dept}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Department Legend</h4>
          <div className="space-y-2">
            {departments.map((dept, index) => {
              const latestData = data[data.length - 1];
              const currentProgress = latestData ? latestData[dept] as number : 0;
              return (
                <div key={dept} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded mr-2" 
                      style={{ backgroundColor: getDepartmentColor(index) }}
                    ></div>
                    <span className="text-sm font-medium">{dept}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{currentProgress.toFixed(1)}%</span>
                    <span className="ml-2 text-xs text-gray-500">current</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Trend Analysis</h4>
          <div className="space-y-3">
            {departments.map((dept, index) => {
              const firstData = data[0];
              const lastData = data[data.length - 1];
              const firstProgress = firstData ? firstData[dept] as number : 0;
              const lastProgress = lastData ? lastData[dept] as number : 0;
              const trend = lastProgress - firstProgress;
              const isImproving = trend > 0;
              
              return (
                <div key={dept} className={`p-3 rounded-lg ${isImproving ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                      {dept}
                    </span>
                    <span className={`font-bold ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                      {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                    </span>
                  </div>
                  <div className={`text-xs ${isImproving ? 'text-green-500' : 'text-red-500'} mt-1`}>
                    {isImproving ? 'Improving' : 'Declining'} over time
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentProgressTrendsChart;


